import cx from 'classnames';
import {get, isEmpty, map, reduce} from 'lodash';
import {Fragment, useCallback} from 'react';
import {useTranslation} from 'react-i18next';

import {Icon, IconName} from 'components';
import {MARGIN_MAP} from 'constants/margin-map';
import {TRANSLATION_KEYS} from 'locale';
import {FormattedPermission, UserPermissions} from 'types';

const SEPARATOR = ',';

type PermissionItemOptionsType = {
    containerClassName?: string;
    className?: string;
    childrenClassName?: string;
    iconName?: IconName;
    iconClassName?: string;
    withSeparator?: boolean;
};

type Props = {
    availablePermissions: UserPermissions;
    formattedPermissions: FormattedPermission[];
    getFormattedPermissions: (permissions) => FormattedPermission[];
};

const COMMON_T_KEYS = TRANSLATION_KEYS.COMMON.ALL;

export const PermissionList: React.FC<Props> = ({availablePermissions, formattedPermissions, getFormattedPermissions}: Props) => {
    const {t} = useTranslation();

    const countAllChildren = useCallback((children: FormattedPermission[]) => reduce(children, (count, child) => {
        if (child?.children) {
            return count + child?.children?.length;
        } else {
            return count + 1;
        }
    }, 0), []);

    const isAllChildrenSelected = useCallback((permission: FormattedPermission) => {
        const availableChildren = getFormattedPermissions(get(availablePermissions, permission.path));
        const allAvailableChildren = countAllChildren(availableChildren);
        const selectedChildren = countAllChildren(permission.children);
        return allAvailableChildren !== 0 && allAvailableChildren === selectedChildren;
    }, [availablePermissions, countAllChildren, getFormattedPermissions]);

    const renderPermissionItem = useCallback((
        label: string, index: number, options: PermissionItemOptionsType = {withSeparator: false}, renderAll: boolean
    ) => {
        const {className, iconName, iconClassName, withSeparator} = options;
        return (
            <div key={`${label}-${index}`} className={cx('tw-flex tw-mt-1', className)}>
                {iconName && <Icon name={iconName} size='xs' className={cx('tw-mr-1 tw-mt-1', iconClassName)} />}
                {t(label)}
                {renderAll && ` (${t(COMMON_T_KEYS.ALL)})`}
                {withSeparator && SEPARATOR}
            </div>
        );
    }, [t]);

    const getRenderOptionsForDepth = useCallback((depth: number, index: number, length: number, hasChildren: boolean) => {
        const options: PermissionItemOptionsType = {};
        if (depth === 1) {
            options.className = 'tw-ts-h5-bold';
        } else if (depth === 2) {
            options.className = 'tw-ts-base-bold tw-text-default-dimmed';
            options.iconName = 'paper-clip';
            if (hasChildren) {
                options.containerClassName = 'tw-flex tw-flex-wrap';
            }
        } else if (depth === 3) {
            options.className = 'tw-text-default-dimmed';
            options.withSeparator = length !== 1 && index !== length - 1;
            if (index === 0) {
                options.iconName = 'list';
                options.iconClassName = 'tw-ml-4';
            } else {
                options.className = [options.className, 'tw-ml-1'].join(' ');
            }
        }
        return options;
    }, []);

    const renderPermissionList = useCallback((permissions: FormattedPermission[], depth = 1, options?: PermissionItemOptionsType) => (
        <div className={options?.containerClassName}>
            {isEmpty(permissions) && renderPermissionItem(t(COMMON_T_KEYS.NO_RESULT), 0, options, false)}
            {map(permissions, (permission, index) => {
                const {label, children} = permission;
                const currentOptions = getRenderOptionsForDepth(depth, index, permissions.length, !!children);
                const isAllSelected = isAllChildrenSelected(permission);

                return (
                    <Fragment key={`${label}-${index}`}>
                        {renderPermissionItem(label, index, currentOptions, isAllSelected)}
                        {children && !isAllSelected &&
                            <div className={cx('tw-flex tw-items-start', currentOptions?.childrenClassName)}>
                                <div
                                    className='tw-flex'
                                    style={{
                                        marginLeft: MARGIN_MAP[depth]
                                    }}
                                >
                                    {renderPermissionList(children, depth + 1, currentOptions)}
                                </div>
                            </div>}
                    </Fragment>
                );

            })}
        </div>
    ), [getRenderOptionsForDepth, isAllChildrenSelected, renderPermissionItem, t]);

    return (
        <div className='tw-w-full tw-mt-2 tw-order-2 -lg-landscape:tw-col-span-3 -lg-landscape:tw-order-none -lg-landscape:tw-mt-0'>
            {renderPermissionList(formattedPermissions)}
        </div>
    );
};